import React, { useEffect, ReactNode, useState } from 'react';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Navigation } from './src/navigation';
import { ThemeProvider, useTheme } from './src/context/ThemeContext';
import { NotificationProvider } from './src/context/NotificationContext';
import { analyticsService } from './src/services/firebase';
import { useNotificationsListener } from './src/hooks/useNotificationsListener';
import AlertDialog from './src/components/AlertDialog';
import { setAlertDialogHandler } from './src/utils/alert';

// Componente que inicializa los servicios
interface ServicesInitializerProps {
  children: ReactNode;
}

const ServicesInitializer = ({ children }: ServicesInitializerProps) => {
  // Inicializa el listener de notificaciones
  useNotificationsListener();
  
  return <>{children}</>;
};

// Componente principal con tema aplicado
const ThemedApp = () => {
  const { isDarkMode } = useTheme();
  
  // Estado para controlar el AlertDialog
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertButtons, setAlertButtons] = useState<Array<{
    text: string;
    onPress: () => void;
    style?: 'default' | 'cancel' | 'destructive';
  }>>([]);

  // Configurar manejador de alertas
  useEffect(() => {
    setAlertDialogHandler((title, message, buttons = [{ text: 'OK' }]) => {
      setAlertTitle(title);
      setAlertMessage(message || '');
      // Aseguramos que onPress siempre sea una función
      const processedButtons = buttons.map(button => ({
        ...button,
        onPress: button.onPress || (() => {})
      }));
      setAlertButtons(processedButtons);
      setAlertVisible(true);
    });
  }, []);
  
  // Inicializa Analytics al iniciar la aplicación
  useEffect(() => {
    // Habilitamos Analytics (inicialmente desactivado en firebase.json)
    (async () => {
      try {
        // Habilitar la recolección de datos de Analytics
        await analyticsService.setAnalyticsCollectionEnabled(true);
        
        // Registrar evento de apertura de la aplicación
        await analyticsService.logEvent('app_open_custom');
        
        // Obtener el ID de instancia para verificar que funciona
        const appInstanceId = await analyticsService.getAppInstanceId();
        console.log('Firebase Analytics inicializado con ID:', appInstanceId);
      } catch (error) {
        console.error('Error al inicializar Firebase Analytics:', error);
      }
    })();
  }, []);

  return (
    <SafeAreaProvider>
      <NotificationProvider>
        <ServicesInitializer>
          <Navigation />
          <StatusBar style={isDarkMode ? "light" : "dark"} />
          
          {/* AlertDialog para web */}
          <AlertDialog
            visible={alertVisible}
            title={alertTitle}
            message={alertMessage}
            buttons={alertButtons}
            onDismiss={() => setAlertVisible(false)}
          />
        </ServicesInitializer>
      </NotificationProvider>
    </SafeAreaProvider>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}
